/**
 * Importar desde una hoja de cálculo.
 *
 * El caso real: Esteban ya lleva sus cuentas en Excel, con columnas
 * INGRESOS · SALDO · DESCUENTO · CONCEPTO. Reescribir eso a mano sería
 * perder el tiempo, y peor: empezaría la app sin historia, y sin historia
 * no hay gráficas ni promedios ni alertas que sirvan.
 *
 * Se copia de Excel y se pega. Al copiar, Excel pone el contenido en el
 * portapapeles separado por tabulaciones, así que eso es lo que se lee.
 */

import { interpretar } from './dinero.js';

/** Nombres que suele tener cada columna, para reconocerlas solas. */
const ALIAS = {
  monto: ['monto', 'valor', 'ingresos', 'ingreso', 'egreso', 'importe', 'cantidad', 'debito', 'credito'],
  concepto: ['concepto', 'descripcion', 'descripción', 'detalle', 'referencia', 'nota'],
  fecha: ['fecha', 'dia', 'día', 'date'],
  saldo: ['saldo', 'balance', 'total'],
  gmf: ['descuento', 'gmf', '4x1000', '4x mil', 'impuesto'],
  categoria: ['categoria', 'categoría', 'rubro', 'tipo'],
};

function normalizar(texto) {
  return String(texto || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')   // quita tildes
    .trim();
}

/**
 * Parte el texto pegado en filas y columnas.
 * Acepta tabulaciones (lo que pega Excel), punto y coma, o comas.
 */
export function partir(texto) {
  const lineas = String(texto || '')
    .split(/\r?\n/)
    .filter((l) => l.trim() !== '');
  if (!lineas.length) return [];

  // Se elige el separador por el que produzca más columnas en la primera fila.
  const candidatos = ['\t', ';', ','];
  const separador = candidatos
    .map((s) => ({ s, n: lineas[0].split(s).length }))
    .sort((a, b) => b.n - a.n)[0].s;

  return lineas.map((l) => l.split(separador).map((c) => c.trim().replace(/^"|"$/g, '')));
}

/**
 * Adivina qué columna es cuál mirando la primera fila.
 * Si la primera fila no parece encabezado, devuelve un mapeo vacío para que
 * el usuario lo arme a mano.
 */
export function detectarColumnas(filas) {
  if (!filas.length) return { encabezado: false, mapa: {} };

  const primera = filas[0].map(normalizar);
  // Es encabezado si al menos una celda coincide con un alias conocido y
  // ninguna parece un número.
  const pareceEncabezado =
    primera.some((c) => Object.values(ALIAS).some((lista) => lista.includes(c))) &&
    !primera.every((c) => /^[\d.,$\s-]+$/.test(c));

  if (!pareceEncabezado) return { encabezado: false, mapa: {} };

  const mapa = {};
  primera.forEach((celda, i) => {
    for (const [campo, alias] of Object.entries(ALIAS)) {
      if (alias.includes(celda) && mapa[campo] === undefined) mapa[campo] = i;
    }
  });
  return { encabezado: true, mapa };
}

/** Interpreta una fecha en los formatos que se usan en Colombia. */
export function interpretarFecha(texto) {
  const s = String(texto || '').trim();
  if (!s) return null;

  // Ya viene en ISO
  if (/^\d{4}-\d{2}-\d{2}/.test(s)) return s.slice(0, 10);

  // dd/mm/aaaa o dd-mm-aaaa. En Colombia el día va PRIMERO; leerlo al revés
  // convertiría el 3 de julio en el 7 de marzo sin que nadie se dé cuenta.
  const m = s.match(/^(\d{1,2})[/\-.](\d{1,2})[/\-.](\d{2,4})/);
  if (m) {
    let [, d, mes, a] = m;
    if (a.length === 2) a = `20${a}`;
    const dia = Number(d);
    const mm = Number(mes);
    // Si el primer número pasa de 12, seguro es el día y el orden es el
    // esperado. Si el segundo pasa de 12, venía al revés (formato gringo).
    if (dia > 12 && mm <= 12) return `${a}-${String(mm).padStart(2, '0')}-${String(dia).padStart(2, '0')}`;
    if (mm > 12 && dia <= 12) return `${a}-${String(dia).padStart(2, '0')}-${String(mm).padStart(2, '0')}`;
    return `${a}-${String(mm).padStart(2, '0')}-${String(dia).padStart(2, '0')}`;
  }

  // Número de serie de Excel: días desde el 30/12/1899.
  if (/^\d{5}$/.test(s)) {
    const base = Date.UTC(1899, 11, 30);
    const d = new Date(base + Number(s) * 86400000);
    return d.toISOString().slice(0, 10);
  }

  const d = new Date(s);
  return Number.isNaN(d.getTime()) ? null : d.toISOString().slice(0, 10);
}

/**
 * Convierte las filas en movimientos listos para revisar.
 *
 * No guarda nada: devuelve un borrador con lo que se pudo leer y lo que no,
 * para que el usuario lo vea antes de aceptarlo. Meter 200 filas a ciegas es
 * la mejor forma de ensuciar una base de datos nueva.
 */
export function construir(filas, mapa, { desdeFila = 1, fechaPorDefecto = null } = {}) {
  const movimientos = [];
  const descartadas = [];

  for (let i = desdeFila; i < filas.length; i++) {
    const fila = filas[i];
    const crudo = (campo) => (mapa[campo] !== undefined ? fila[mapa[campo]] : '');

    const montoTexto = crudo('monto');
    const monto = interpretar(montoTexto);

    if (monto === null || monto === 0) {
      descartadas.push({ fila: i + 1, motivo: 'sin monto legible', contenido: fila.join(' | ') });
      continue;
    }

    const concepto = String(crudo('concepto') || '').trim();
    const fecha = interpretarFecha(crudo('fecha')) || fechaPorDefecto;
    const gmf = Math.abs(interpretar(crudo('gmf')) ?? 0);

    movimientos.push({
      // En una hoja de cálculo el signo es lo que distingue entrada de salida.
      tipo: monto > 0 ? 'ingreso' : 'egreso',
      monto: Math.abs(monto),
      concepto,
      fecha,
      gmf,
      // Se marca el origen para poder deshacer la importación completa
      // después, si algo salió mal.
      origen_dato: 'importado',
      filaOriginal: i + 1,
      sinFecha: !fecha,
    });
  }

  return { movimientos, descartadas };
}

/** Resumen para mostrar antes de aceptar. */
export function resumir(movimientos) {
  const ingresos = movimientos.filter((m) => m.tipo === 'ingreso');
  const egresos = movimientos.filter((m) => m.tipo === 'egreso');
  const fechas = movimientos.map((m) => m.fecha).filter(Boolean).sort();

  return {
    total: movimientos.length,
    ingresos: ingresos.length,
    egresos: egresos.length,
    sumaIngresos: ingresos.reduce((t, m) => t + m.monto, 0),
    sumaEgresos: egresos.reduce((t, m) => t + m.monto, 0),
    gmfTotal: movimientos.reduce((t, m) => t + (m.gmf || 0), 0),
    sinFecha: movimientos.filter((m) => !m.fecha).length,
    desde: fechas[0] ?? null,
    hasta: fechas[fechas.length - 1] ?? null,
  };
}
